import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { useHomeFeed } from '../hooks/useHomeFeed';
import { useUnseenAchievements } from '../hooks/useUnseenAchievements';
import * as analytics from '../lib/analytics';
import { AppHeader } from './AppHeader';
import EmailVerifyBanner from './EmailVerifyBanner';
import { NextMatchCard, TopMatchesList, PendingActionsCard, NewUserDiscoveryCard, PersonalStatsCard, FinalizationBanner, NoGroupCard } from './home';
import { useShouldShowNoGroupCard } from '../hooks/useShouldShowNoGroupCard';
import { MysteryCardModal } from './achievements/MysteryCardModal';
import { AchievementRevealPopup } from './achievements/AchievementRevealPopup';
import type { Achievement } from '../types/achievement';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { data, loading, error, refresh } = useHomeFeed();

  const { unseen, markRevealed } = useUnseenAchievements();
  const [revealed, setRevealed] = useState<Achievement | null>(null);
  const [dismissedSession, setDismissedSession] = useState(false);
  const currentAchievement = !dismissedSession && unseen.length > 0 ? unseen[0] : null;
  const seenTrackedRef = useRef<string | null>(null);

  const { show: showNoGroupCard } = useShouldShowNoGroupCard();

  useEffect(() => {
    if (!currentAchievement) return;
    if (seenTrackedRef.current === currentAchievement.id) return;
    seenTrackedRef.current = currentAchievement.id;
    analytics.track('achievement_mystery_seen', {
      achievementId: currentAchievement.id,
      key: currentAchievement.key,
      queuePosition: 1,
      queueTotal: unseen.length,
    });
  }, [currentAchievement, unseen.length]);

  const handleReveal = async () => {
    if (!currentAchievement) return;
    const enriched = await markRevealed(currentAchievement.id);
    if (enriched) {
      analytics.track('achievement_revealed', {
        achievementId: enriched.id,
        key: enriched.key,
      });
      setRevealed(enriched);
    }
  };

  const handleContinue = () => setRevealed(null);
  const handleDismiss = () => setDismissedSession(true);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        color: theme.colors.text,
      }}
    >
      <AppHeader />
      <EmailVerifyBanner />

      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '16px' : theme.spacing.lg,
          marginTop: isMobile ? 60 : 0,
        }}
      >
        <div
          style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.card,
            padding: isMobile ? '16px' : theme.spacing.lg,
            boxShadow: theme.colors.shadow,
            border: `1px solid ${theme.colors.border}`,
          }}
        >
          <h2
            style={{
              margin: '0 0 24px 0',
              color: theme.colors.primary,
              fontFamily: theme.fonts.heading,
              fontSize: theme.fonts.sizeTitle,
              letterSpacing: theme.fonts.letterSpacingWide,
              textTransform: 'uppercase',
            }}
          >
            ⚽ Home
          </h2>

          {loading && !data && (
            <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.textSecondary }}>
              Cargando tu dashboard...
            </div>
          )}

          {error && !data && (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <p style={{ color: theme.colors.danger, marginBottom: 12 }}>{error}</p>
              <button
                type="button"
                onClick={refresh}
                style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.background,
                  border: 'none',
                  borderRadius: theme.borderRadius.button,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reintentar
              </button>
            </div>
          )}

          {data && user && (
            <>
              {data.needsFinalization && (
                <FinalizationBanner match={data.needsFinalization} />
              )}

              <NextMatchCard
                match={data.nextMatch}
                currentUserId={user.id}
              />

              {showNoGroupCard && <NoGroupCard variant="home" />}

              {data.topMatches && data.topMatches.length > 0 && (
                <TopMatchesList
                  matches={data.topMatches}
                  totalCount={data.allMatches?.length ?? 0}
                />
              )}

              {data.pendingActions && data.pendingActions.length > 0 && (
                <PendingActionsCard pendingActions={data.pendingActions} />
              )}

              <PersonalStatsCard userId={user.id} />

              {/* New-user empty-state: feed totalmente vacío → mostrar nudges */}
              {!data.nextMatch &&
                (!data.topMatches || data.topMatches.length === 0) &&
                (!data.pendingActions || data.pendingActions.length === 0) && (
                  <NewUserDiscoveryCard />
                )}

              {error && data && (
                <p style={{ marginTop: 16, fontSize: 12, color: theme.colors.textSecondary, textAlign: 'center' }}>
                  Mostrando datos guardados. {error}
                </p>
              )}

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={refresh}
                  style={{
                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                    background: 'none',
                    border: `1px solid ${theme.colors.border}`,
                    color: theme.colors.textSecondary,
                    borderRadius: theme.borderRadius.button,
                    fontSize: theme.fonts.sizeSmall,
                    cursor: 'pointer',
                  }}
                >
                  Actualizar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {currentAchievement && !revealed && (
        <MysteryCardModal
          open
          count={unseen.length}
          current={1}
          onReveal={handleReveal}
          onClose={handleDismiss}
        />
      )}
      {revealed && (
        <AchievementRevealPopup achievement={revealed} onContinue={handleContinue} />
      )}
    </div>
  );
};

export default HomePage;
